import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import Select, { components } from "react-select";
import { useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import PerformanceGroupHeatmap from "./PerformanceTimelineHeatmap";
import Loader from "../../Components/Common/Loader";

const getTimelineSection = (response, timelineMode) => {
  if (timelineMode === "top_ten_items") {
    return response?.data?.top_ten_items;
  }
  return response?.data?.performance_group;
};

const buildTopTenItemsRows = (section) => {
  const items = section?.items || [];
  if (!items.length) return [];
  const dateCount = Math.max(0, ...items.map((it) => (it.days || []).length));
  const rankByDateAndItem = [];
  for (let dayIdx = 0; dayIdx < dateCount; dayIdx++) {
    const daySnapshot = items.map((item, itemIdx) => ({
      itemIdx,
      sold: item.days?.[dayIdx]?.item_sold ?? 0,
    }));
    const sorted = [...daySnapshot].sort((a, b) => b.sold - a.sold);
    const rankMap = {};
    sorted.forEach((entry, position) => {
      rankMap[entry.itemIdx] = position + 1;
    });
    rankByDateAndItem.push(rankMap);
  }
  return items.map((item, itemIdx) => ({
    dish_id: item.item_id,
    dish_name: item.item_name,
    sold_count: item.total_item_sold ?? 0,
    timeline: (item.days || []).map((day, dayIdx) => {
      const sold = day.item_sold ?? 0;
      const rank = rankByDateAndItem[dayIdx]?.[itemIdx];
      let group = "Not Sold";
      if (sold > 0) {
        group = rank <= 5 ? "Top 5" : "Top 6-10";
      }
      return {
        row_dt: day.display_date,
        performance_group: group,
        item_name: item.item_name,
        sold_count: sold,
        rank: sold > 0 ? rank : null,
        winner: day.winner || "No",
      };
    }),
  }));
};

const extractDishRowsFromResponse = (
  response,
  timelineMode = "performance_group",
) => {
  const section = getTimelineSection(response, timelineMode);
  if (timelineMode === "top_ten_items") {
    return buildTopTenItemsRows(section).filter(
      (row) => row.dish_id && row.timeline.length > 0,
    );
  }
  const items = section?.items || [];
  return items
    .map((item) => ({
      dish_id: item.item_id,
      dish_name: item.item_name,
      sold_count: item.total_item_sold ?? 0,
      timeline: (item.days || []).map((day) => ({
        row_dt: day.display_date,
        performance_group: day.performance_group || "Not Sold",
        item_name: item.item_name,
        sold_count: day.item_sold ?? 0,
        winner: day.winner || "No",
      })),
    }))
    .filter((row) => row.dish_id && row.timeline.length > 0);
};

const extractWeekMarkers = (response, timelineMode = "performance_group") => {
  const section = getTimelineSection(response, timelineMode);
  return section?.weeks || null;
};

const extractTopDishIds = (response) => {
  const items = response?.data?.performance_group?.items || [];
  return items.map((item) => item.item_id).filter(Boolean);
};

const extractDistributionCounts = (response) => {
  const kpi =
    response?.data?.performance_kpi ||
    response?.data?.result?.performance_kpi ||
    response?.performance_kpi ||
    {};
  return {
    Star: Number(kpi.Star) || 0,
    "Cash Cow": Number(kpi["Cash Cow"]) || 0,
    Puzzle: Number(kpi.Puzzle) || 0,
    Dog: Number(kpi.Dog) || 0,
  };
};

const ALL_ITEMS_VALUE = "__all__";
const ALL_ITEMS_OPTION = { value: ALL_ITEMS_VALUE, label: "All Items" };

const formatDishOption = (dish) => ({
  value: dish.dish_id,
  label: dish.dish_name,
  ...dish,
});

const getDishId = (dish) => Number(dish?.dish_id ?? dish?.value);

const areAllDishesSelected = (selected, allDishes) => {
  if (!allDishes.length || !selected.length) return false;
  const selectedIds = new Set(selected.map(getDishId).filter(Boolean));
  return allDishes.every((dish) => selectedIds.has(getDishId(dish)));
};

// Loader component for distribution box
const DistributionLoader = () => (
  <div className="text-center py-4">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
    <p className="mt-2 text-muted">Loading distribution data...</p>
  </div>
);

// Loader component for chart
const ChartLoader = () => (
  <div className="text-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
    <p className="mt-3 text-muted">Loading performance timeline...</p>
  </div>
);

const DishBenchmarking = () => {
  const [courses, setCourses] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedDishes, setSelectedDishes] = useState([]);
  const [openSelect, setOpenSelect] = useState(null);
  const courseSelectRef = useRef(null);
  const dishSelectRef = useRef(null);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [performanceTimeline, setPerformanceTimeline] =
    useState("performance_group");
  const [isOverall, setIsOverall] = useState(false);
  const [benchmarkingResponse, setBenchmarkingResponse] = useState(null);
  const [distributionCounts, setDistributionCounts] = useState({
    Star: 0,
    "Cash Cow": 0,
    Puzzle: 0,
    Dog: 0,
  });
  const [loading] = useState(false);
  const fetchSeqRef = useRef(0);

  // Get courses from Redux
  const { filterData } = useSelector((state) => ({
    filterData: state.Dashboard.filterData,
  }));

  // Fetch dishes from API
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setDistributionLoading(true);
        const response = await postDataAPI({
          serviceName: "kpi_data",
          op: "fetch_dish_benchmarking_filters",
          course_ids: [],
        });
        setDishes(response?.data?.dishes || []);
        setDistributionLoading(false);
      } catch (error) {
        console.error("DISH BENCHMARKING FILTER API ERROR:", error);
        setDistributionLoading(false);
      }
    };
    fetchFilters();
  }, []);

  // Set courses from Redux when filterData changes
  useEffect(() => {
    if (filterData?.courses && filterData.courses.length > 0) {
      const courseOptions = filterData.courses.map((course) => ({
        value: parseInt(course.value),
        label: course.label,
      }));
      setCourses(courseOptions);
    }
  }, [filterData]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const target = event.target;
      const clickedInCourseSelect = courseSelectRef.current?.contains(target);
      const clickedInDishSelect = dishSelectRef.current?.contains(target);
      const clickedInMenu =
        target instanceof Element && target.closest(".react-select__menu");

      if (!clickedInCourseSelect && !clickedInDishSelect && !clickedInMenu) {
        setOpenSelect(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Custom Option component
  const CustomOption = (props) => (
    <components.Option {...props}>
      <div className="d-flex align-items-center gap-2">
        <input type="checkbox" checked={props.isSelected} readOnly />
        <span>{props.label}</span>
      </div>
    </components.Option>
  );

  // Select styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "42px",
      borderRadius: "8px",
      boxShadow: "none",
      cursor: "pointer",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // Filter dishes by course
  const filteredDishes =
    selectedCourses.length > 0
      ? dishes.filter((dish) =>
          selectedCourses.some(
            (course) => Number(course.value) === Number(dish.course_id),
          ),
        )
      : dishes;

  const dishOptions = filteredDishes.map(formatDishOption);
  const allItemsChecked = areAllDishesSelected(selectedDishes, dishOptions);
  const dishSelectOptions = dishOptions.length
    ? [ALL_ITEMS_OPTION, ...dishOptions]
    : [];
  const dishSelectValue = allItemsChecked
    ? [ALL_ITEMS_OPTION, ...selectedDishes]
    : selectedDishes;

  // Fetch benchmarking data
  const fetchBenchmarkingData = async ({
    overall = false,
    courseIds = [],
    dishIds = [],
    timeline = "performance_group",
    showDistributionLoader = true,
  }) => {
    const fetchSeq = ++fetchSeqRef.current;
    try {
      // Show chart loader for all API calls
      setChartLoading(true);

      // Show distribution loader only when explicitly requested
      if (showDistributionLoader) {
        setDistributionLoading(true);
      }

      const payload = {
        serviceName: "kpi_data",
        op: "fetch_dish_benchmarking_data",
        course_ids: courseIds,
        dish_ids: dishIds,
        over_all: overall,
        performance_timeline: timeline,
      };
      const response = await postDataAPI(payload);
      if (fetchSeq !== fetchSeqRef.current) {
        return null;
      }
      setBenchmarkingResponse(response);
      setDistributionCounts(extractDistributionCounts(response));
      return response;
    } catch (error) {
      if (fetchSeq !== fetchSeqRef.current) {
        return null;
      }
      console.error("DISH BENCHMARKING DATA API ERROR:", error);
      setBenchmarkingResponse({
        error: true,
        message: error?.message || "API request failed",
      });
      return null;
    } finally {
      if (fetchSeq === fetchSeqRef.current) {
        setChartLoading(false);
        setDistributionLoading(false);
      }
    }
  };

  // Initial benchmarking API call
  useEffect(() => {
    fetchBenchmarkingData({
      overall: false,
      courseIds: [],
      dishIds: [],
      timeline: "performance_group",
    });
  }, []);

  // Course change handler
  const handleCourseChange = async (value) => {
    const newCourses = value || [];
    const keepAllItems = allItemsChecked;
    setSelectedCourses(newCourses);
    const courseIds = newCourses.map((course) => course.value);
    const nextFilteredDishes =
      newCourses.length > 0
        ? dishes.filter((dish) =>
            newCourses.some(
              (course) => Number(course.value) === Number(dish.course_id),
            ),
          )
        : dishes;
    const nextDishOptions = nextFilteredDishes.map(formatDishOption);

    if (keepAllItems && nextDishOptions.length) {
      setSelectedDishes(nextDishOptions);
      setIsOverall(false);
      setPerformanceTimeline("performance_group");
      await fetchBenchmarkingData({
        overall: false,
        courseIds,
        dishIds: nextDishOptions.map((dish) => dish.dish_id),
        timeline: "performance_group",
      });
      return;
    }

    if (!newCourses.length) {
      setSelectedDishes([]);
      setIsOverall(false);
      setPerformanceTimeline("performance_group");
      await fetchBenchmarkingData({
        overall: false,
        courseIds: [],
        dishIds: [],
        timeline: "performance_group",
      });
      return;
    }
    const validCourseIds = courseIds.map(Number);
    const newSelectedDishes = selectedDishes.filter((dish) =>
      validCourseIds.includes(Number(dish.course_id)),
    );
    setSelectedDishes(newSelectedDishes);
    setIsOverall(false);
    setPerformanceTimeline("performance_group");
    await fetchBenchmarkingData({
      overall: false,
      courseIds,
      dishIds: newSelectedDishes.map((dish) => dish.dish_id),
      timeline: "performance_group",
    });
  };

  // Dish change handler - shows both loaders
  const handleDishChange = async (value, actionMeta) => {
    const clickedAllItems = actionMeta?.option?.value === ALL_ITEMS_VALUE;
    let newDishes;

    if (clickedAllItems && actionMeta?.action === "select-option") {
      newDishes = dishOptions;
    } else if (clickedAllItems && actionMeta?.action === "deselect-option") {
      newDishes = [];
    } else {
      newDishes = (value || []).filter(
        (item) => item.value !== ALL_ITEMS_VALUE,
      );
    }

    setSelectedDishes(newDishes);
    setIsOverall(false);
    setPerformanceTimeline("performance_group");
    await fetchBenchmarkingData({
      overall: false,
      courseIds: selectedCourses.map((course) => course.value),
      dishIds: newDishes.map((dish) => dish.dish_id),
      timeline: "performance_group",
      showDistributionLoader: true, // Show distribution loader
    });
  };

  // Top 10 Overall handler
  const handleTopTenOverall = async () => {
    setIsOverall(true);
    const response = await fetchBenchmarkingData({
      overall: true,
      courseIds: selectedCourses.map((course) => course.value),
      dishIds: [],
      timeline: "performance_group",
    });
    setPerformanceTimeline("performance_group");
    const topDishIds = extractTopDishIds(response);
    if (!topDishIds.length) {
      console.warn("Top 10 Overall: couldn't find dish ids in the response");
      return;
    }
    const rawItems = response?.data?.performance_group?.items || [];
    const resolvedDishes = topDishIds.map((id) => {
      const master = dishes.find((d) => Number(d.dish_id) === Number(id));
      if (master) return formatDishOption(master);
      const raw = rawItems.find((it) => Number(it.item_id) === Number(id));
      const name = raw?.item_name || `Dish #${id}`;
      return { value: id, label: name, dish_id: id, dish_name: name };
    });
    setSelectedDishes(resolvedDishes);
  };

  // Top 10 Items handler - shows only chart loader
  const handleTopTenItems = async () => {
    if (!selectedDishes.length) return;
    setPerformanceTimeline("top_ten_items");
    await fetchBenchmarkingData({
      overall: isOverall,
      courseIds: selectedCourses.map((course) => course.value),
      dishIds: selectedDishes.map((dish) => dish.dish_id),
      timeline: "top_ten_items",
      showDistributionLoader: false, // Don't show distribution loader
    });
  };

  // Performance Group handler - shows only chart loader
  const handlePerformanceGroup = async () => {
    setPerformanceTimeline("performance_group");
    await fetchBenchmarkingData({
      overall: isOverall,
      courseIds: selectedCourses.map((course) => course.value),
      dishIds: selectedDishes.map((dish) => dish.dish_id),
      timeline: "performance_group",
      showDistributionLoader: false, // Don't show distribution loader
    });
  };

  // Remove dish handler
  const removeDish = async (dishId) => {
    const newDishes = selectedDishes.filter(
      (dish) => Number(dish.dish_id) !== Number(dishId),
    );
    setSelectedDishes(newDishes);
    setIsOverall(false);
    setPerformanceTimeline("performance_group");
    await fetchBenchmarkingData({
      overall: false,
      courseIds: selectedCourses.map((course) => course.value),
      dishIds: newDishes.map((dish) => dish.dish_id),
      timeline: "performance_group",
    });
  };

  // Clear dishes handler
  const clearDishes = async () => {
    setSelectedDishes([]);
    setIsOverall(false);
    setPerformanceTimeline("performance_group");
    await fetchBenchmarkingData({
      overall: false,
      courseIds: selectedCourses.map((course) => course.value),
      dishIds: [],
      timeline: "performance_group",
    });
  };

  // Heatmap rows derived from API response
  const heatmapRows = useMemo(() => {
    const rows = extractDishRowsFromResponse(
      benchmarkingResponse,
      performanceTimeline,
    );
    return rows.map((row) => {
      const master = dishes.find(
        (d) => Number(d.dish_id) === Number(row.dish_id),
      );
      return {
        ...row,
        category: master?.category || master?.course_name || "",
      };
    });
  }, [benchmarkingResponse, performanceTimeline, dishes]);

  // Week markers for heatmap
  const weekMarkers = useMemo(
    () => extractWeekMarkers(benchmarkingResponse, performanceTimeline),
    [benchmarkingResponse, performanceTimeline],
  );

  const heatmapMode =
    performanceTimeline === "top_ten_items"
      ? "top_ten_items"
      : "performance_group";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Dish Benchmarking" pageTitle="Dashboard" />
        {/* Filter Card */}
        <Card>
          <CardBody>
            <Row className="align-items-start g-3">
              {/* Course Select */}
              <Col md={3}>
                <div ref={courseSelectRef}>
                  <Select
                    isMulti
                    isSearchable={false}
                    options={courses}
                    value={selectedCourses}
                    onChange={handleCourseChange}
                    onFocus={() => setOpenSelect("courses")}
                    menuIsOpen={openSelect === "courses"}
                    onMenuOpen={() => setOpenSelect("courses")}
                    onMenuClose={() =>
                      setOpenSelect((current) =>
                        current === "courses" ? null : current,
                      )
                    }
                    placeholder={
                      selectedCourses.length
                        ? `Courses: ${selectedCourses.length} selected`
                        : "All Courses"
                    }
                    controlShouldRenderValue={false}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ Option: CustomOption }}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    classNamePrefix="react-select"
                  />
                </div>
              </Col>

              {/* Dish Select */}
              <Col md={3}>
                <div ref={dishSelectRef}>
                  <Select
                    isMulti
                    isSearchable={false}
                    options={dishSelectOptions}
                    value={dishSelectValue}
                    onChange={handleDishChange}
                    onFocus={() => setOpenSelect("dishes")}
                    menuIsOpen={openSelect === "dishes"}
                    onMenuOpen={() => setOpenSelect("dishes")}
                    onMenuClose={() =>
                      setOpenSelect((current) =>
                        current === "dishes" ? null : current,
                      )
                    }
                    placeholder={
                      selectedDishes.length
                        ? `Dishes: ${selectedDishes.length} selected`
                        : "All Dishes"
                    }
                    controlShouldRenderValue={false}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ Option: CustomOption }}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    classNamePrefix="react-select"
                  />
                </div>
              </Col>

              {/* Buttons */}
              <Col
                md={6}
                className="d-flex justify-content-end align-items-center gap-3"
              >
                <button
                  type="button"
                  className={`btn ${isOverall ? "btn-danger" : "btn-light"}`}
                  onClick={handleTopTenOverall}
                  disabled={loading || chartLoading}
                >
                  ⚡ Top 10 Overall
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={clearDishes}
                  disabled={loading || chartLoading}
                >
                  Clear dishes
                </button>
              </Col>
            </Row>

            {/* Selected Dishes Chips */}
            {selectedDishes.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-3">
                {allItemsChecked ? (
                  <div
                    key={ALL_ITEMS_VALUE}
                    onClick={clearDishes}
                    className="d-flex align-items-center gap-1 px-2 py-1 bg-light rounded-pill"
                    style={{ cursor: "pointer"}}
                  >
                    <span>All Items</span>
                    <span role="button">×</span>
                  </div>
                ) : (
                  selectedDishes.map((dish) => (
                    <div
                      key={dish.dish_id}
                      onClick={() => removeDish(dish.dish_id)}
                      className="d-flex align-items-center gap-1 px-2 py-1 bg-light rounded-pill"
                      style={{ cursor: "pointer"}}
                    >
                      <span>{dish.dish_name}</span>
                      <span role="button">×</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Performance Group Distribution */}
        <Card>
          <CardBody>
            <Row className="my-3">
              <Col lg={12}>
                <h6 className="mb-3">
                  Performance Group Distribution (Last 60 Days)
                </h6>
              </Col>
              {distributionLoading ? (
                <Col lg={12}>
                  <DistributionLoader />
                </Col>
              ) : (
                <>
                  {/* Star */}
                  <Col lg={3} md={6} className="mb-3">
                    <div
                      className="border p-3 text-center rounded"
                      style={{ backgroundColor: "#f0fdf4" }}
                    >
                      <h5>Star</h5>
                      <span style={{ fontSize: "20px", padding: "8px 16px" }}>
                        {distributionCounts.Star}
                      </span>
                    </div>
                  </Col>
                  {/* Cash Cow */}
                  <Col lg={3} md={6} className="mb-3">
                    <div
                      className="border p-3 text-center rounded"
                      style={{ backgroundColor: "#fefce8" }}
                    >
                      <h5>Cash Cow</h5>
                      <span style={{ fontSize: "20px", padding: "8px 16px" }}>
                        {distributionCounts["Cash Cow"]}
                      </span>
                    </div>
                  </Col>
                  {/* Puzzle */}
                  <Col lg={3} md={6} className="mb-3">
                    <div
                      className="border p-3 text-center rounded"
                      style={{ backgroundColor: "#f9fafb" }}
                    >
                      <h5>Puzzle</h5>
                      <span style={{ fontSize: "20px", padding: "8px 16px" }}>
                        {distributionCounts.Puzzle}
                      </span>
                    </div>
                  </Col>
                  {/* Dog */}
                  <Col lg={3} md={6} className="mb-3">
                    <div
                      className="border p-3 text-center rounded"
                      style={{ backgroundColor: "#fef2f2" }}
                    >
                      <h5>Dog</h5>
                      <span style={{ fontSize: "20px", padding: "8px 16px" }}>
                        {distributionCounts.Dog}
                      </span>
                    </div>
                  </Col>
                </>
              )}
            </Row>
          </CardBody>
        </Card>

        {/* 60 Day Performance Timeline */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center z-index-1">
              <div>
                <h6 className="mb-1">60-Day Performance Timeline</h6>
              </div>
              <div
                className="d-flex align-items-center p-1 rounded"
                style={{ backgroundColor: "#f1f3f5" }}
              >
                <button
                  type="button"
                  className="border-0 rounded px-3 py-1"
                  style={{
                    backgroundColor:
                      performanceTimeline === "performance_group"
                        ? "#fff"
                        : "transparent",
                    color:
                      performanceTimeline === "performance_group"
                        ? "#1f2937"
                        : "#6b7280",
                    fontWeight:
                      performanceTimeline === "performance_group"
                        ? "500"
                        : "400",
                  }}
                  onClick={handlePerformanceGroup}
                  disabled={chartLoading}
                >
                  Performance Group
                </button>

                <button
                  type="button"
                  className="border-0 rounded px-3 py-1"
                  style={{
                    backgroundColor:
                      performanceTimeline === "top_ten_items"
                        ? "#fff"
                        : "transparent",
                    color:
                      performanceTimeline === "top_ten_items"
                        ? "#1f2937"
                        : "#6b7280",
                    fontWeight:
                      performanceTimeline === "top_ten_items" ? "500" : "400",
                  }}
                  onClick={handleTopTenItems}
                  disabled={!selectedDishes.length || chartLoading}
                >
                  Top 10 Items
                </button>
              </div>
            </div>
          </CardHeader>
          <CardBody className="px-3 pb-3 pt-2">
            {chartLoading ? (
              <ChartLoader />
            ) : heatmapRows.length > 0 ? (
              <PerformanceGroupHeatmap
                rows={heatmapRows.map((row) => ({
                  title: row.dish_name,
                  subtitle: `${row.category ? row.category + " · " : ""}${row.sold_count} sold`,
                  timeline: row.timeline,
                }))}
                mode={heatmapMode}
                weekMarkers={weekMarkers}
                bare
                showLegend
              />
            ) : (
              <div
                style={{
                  padding: "30px 20px",
                  textAlign: "center",
                  color: "#6b7280",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px dashed #d1d5db",
                }}
              >
                Select one or more dishes, or click "Top 10 Overall", to see the
                timeline.
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default DishBenchmarking;